import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { faqPage, webPage, breadcrumb } from "@/lib/jsonLd";
import { getFaqAll } from "@/data/faq";
import { useT } from "@/i18n/LanguageContext";
import StoreBadges from "@/components/StoreBadges";

const TITLE_BY_LANG = {
  en: "FAQ: AI Stoic Coach, Wisdom & Self-Knowledge | StoicOs Virtue Guide",
  nl: "FAQ: AI-Stoïcijnse Coach, Wijsheid & Zelfkennis | StoicOs Virtue Guide",
};

const DESCRIPTION_BY_LANG = {
  en: "Answers to common questions about Virtue Guide: the best AI Stoic coach for wisdom, self-reflection, character development, emotional resilience and better decisions. Marcus Aurelius, Seneca, Epictetus.",
  nl: "Antwoorden op veelgestelde vragen over Virtue Guide: de AI-Stoïcijnse coach voor wijsheid, zelfreflectie, karakterontwikkeling, emotionele veerkracht en betere beslissingen. Marcus Aurelius, Seneca, Epictetus.",
};

export default function Faq() {
  const [openId, setOpenId] = useState(null);
  const { t, lang } = useT();
  const items = getFaqAll(lang);

  useSEO({
    title: TITLE_BY_LANG[lang] || TITLE_BY_LANG.en,
    description: DESCRIPTION_BY_LANG[lang] || DESCRIPTION_BY_LANG.en,
    canonical: langCanonical("/faq", lang),
    alternates: langAlternates("/faq"),
    og: {
      title: TITLE_BY_LANG[lang] || TITLE_BY_LANG.en,
      description: DESCRIPTION_BY_LANG[lang] || DESCRIPTION_BY_LANG.en,
    },
    jsonLd: [
      faqPage(items),
      webPage({
        name: TITLE_BY_LANG[lang] || TITLE_BY_LANG.en,
        description: DESCRIPTION_BY_LANG[lang] || DESCRIPTION_BY_LANG.en,
        url: "/faq",
        breadcrumbItems: [
          { name: "Home", url: "/" },
          { name: "FAQ", url: "/faq" },
        ],
      }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: "FAQ", url: "/faq" },
      ]),
    ],
  });

  return (
    <main data-testid="faq-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-8">{t("faq.eyebrow")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-bone tracking-tight leading-[1.0]">
          {t("faq.h2a")} <br/><span className="italic text-gold">{t("faq.h2b")}</span>
        </h1>
        <p className="mt-10 max-w-2xl text-bone/75 font-light text-lg leading-relaxed">
          {t("faq.pageIntro")}
        </p>

        <div className="gold-rule my-16" />

        <ul className="space-y-4" data-testid="faq-list">
          {items.map((row) => {
            const isOpen = openId === row.id;
            return (
              <li
                key={row.id}
                id={row.id}
                data-testid={`faq-item-${row.id}`}
                className="border border-white/10 hover:border-gold/30 transition-colors bg-ink"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : row.id)}
                  aria-expanded={isOpen}
                  aria-controls={`${row.id}-answer`}
                  data-testid={`faq-toggle-${row.id}`}
                  className="w-full flex items-center justify-between gap-6 text-left px-6 md:px-8 py-6"
                >
                  <span className="font-serif text-xl md:text-2xl text-bone leading-snug tracking-tight">{row.q}</span>
                  <ChevronDown
                    size={20}
                    strokeWidth={1.5}
                    color="#C5A059"
                    className={`flex-shrink-0 transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  id={`${row.id}-answer`}
                  className="grid transition-[grid-template-rows] duration-700 ease-[cubic-bezier(.2,.7,.2,1)]"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 md:px-8 pb-7 text-fog font-light leading-[1.85]">{row.a}</p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-24 p-10 border border-gold/20 bg-ink-elevated/40 text-center">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">{t("faq.cta.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-bone tracking-tight leading-tight">
            {t("faq.cta.h2a")}<span className="italic text-gold">{t("faq.cta.h2b")}</span>
          </h2>
          <p className="mt-5 text-fog font-light max-w-md mx-auto leading-relaxed">
            {t("faq.cta.body")}
          </p>
          <div className="mt-8 flex justify-center">
            <StoreBadges align="center" />
          </div>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-gold hover:text-gold-hover transition-colors"
            data-testid="faq-about-link"
          >
            {t("faq.cta.aboutLink")} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </main>
  );
}
